import { showError } from "../../features/actions";
import { buildPublicUrl, getCloudData, getCloudProjectList, getCloudUser, serverFeatureAvailable, setCloudData, setCloudProjectList } from "../../features/cloudstorage/cloud";
import { checkUser } from "../../features/cloudstorage/user";
import { getLocalStorageProjectList } from "../../features/localstorage/loader";
import { listData, loadCloudData, loadDataFromCloud } from "../../features/cloudstorage/wp-json";
import { cleanLocalProjects } from "../../features/localstorage/clean";
import { hasLocalUserData, loadLocalProjectList, loadUserCodeFromLS } from "../../features/localstorage/loader";
import { saveUserCodeToLS } from "../../features/localstorage/saver";
import { solutionProjectRestricted } from "../../features/restrictions";
import { initResultCode } from "../../features/resultcode";
import { hasUserStats, loadLocalStats } from "../../features/stats";
import { getAppCfg } from "../app-cfg";
import { SHARE_PROJECT_STATE } from "../constants";
import { cloudProjectId, cloudProjectIdWithFallback, getProjectMetaFromCloudId, isCloudProjectId } from "../project/projectid";
import { findProjectsWithCategory, getIconForProject } from "../project/projectslist";
import { ProjectListData } from "../types";
import { getComponentData, setComponentData } from "../views";
import { updateProjectListData } from "./saver";

/**
 * Load cloud data and check if it contains actual code content
 * @param cloudId The cloud project ID to load
 * @param publicProject Whether this is a public project
 * @returns Promise with success status and data, plus a flag indicating if code exists
 */
async function loadCloudDataWithCodeCheck(
  cloudId: string, 
  publicProject: boolean
): Promise<{success: boolean, data?: any, error?: string, status?: number, hasCode: boolean}> {
  const result = await loadCloudData(cloudId, publicProject);
  
  if (!result.success || !result.data) {
    return { ...result, hasCode: false };
  }

  // Unpack and check if code exists
  try {
    const { decompressBase64Async } = await import("../../features/zipping");
    let hasCode = false;
    let decompressedContent: string | null = null;
    
    // Use Promise to handle the callback-based decompressBase64Async
    await new Promise<void>((resolve) => {
      decompressBase64Async(result.data!, (content: string) => {
        decompressedContent = content;
        resolve();
      });
    });
    
    if (decompressedContent) {
      try {
        const data = JSON.parse(decompressedContent);
        // Check if code exists and is not empty
        const code = data.code;
        const sources = data.sources;
        const tabs = data.tabs || [];
        
        // Check if there's actual code content
        // Code is considered present if:
        // 1. code is a non-empty string (even whitespace counts as "saved code")
        // 2. sources is a non-empty string
        // 3. tabs array has entries with code
        hasCode = (
          (typeof code === 'string' && code.length > 0) ||
          (typeof sources === 'string' && sources.length > 0) ||
          (Array.isArray(tabs) && tabs.length > 0 && tabs.some((tab: any) => {
            if (tab && typeof tab === 'object') {
              return (tab.code && typeof tab.code === 'string' && tab.code.length > 0) ||
                     (tab.source && typeof tab.source === 'string' && tab.source.length > 0);
            }
            return false;
          }))
        );
        
        const codeType = code !== undefined ? typeof code : 'undefined';
        const codeLength = typeof code === 'string' ? code.length : (code !== undefined && code !== null ? String(code).length : 0);
        const sourcesLength = typeof sources === 'string' ? sources.length : (sources !== undefined && sources !== null ? String(sources).length : 0);
        const codePreview = typeof code === 'string' ? code.substring(0, 50).replace(/\n/g, '\\n').replace(/\r/g, '\\r') : (code !== undefined && code !== null ? String(code).substring(0, 50) : 'null/undefined');
        
        console.log(`Code check for ${cloudId}: hasCode=${hasCode}, codeType=${codeType}, codeLength=${codeLength}, sourcesLength=${sourcesLength}, tabsCount=${Array.isArray(tabs) ? tabs.length : 0}, codePreview="${codePreview}"`);
      } catch (e) {
        console.warn('Failed to parse cloud data for code check:', e);
        hasCode = false;
      }
    }
    
    return { ...result, hasCode };
  } catch (e) {
    console.warn('Failed to decompress cloud data for code check:', e);
    return { ...result, hasCode: false };
  }
}

export const loadUserData = async (publicProject : boolean) : Promise<void> => {
  if (hasLocalUserData()) {
    await loadUserCodeFromLS();
  }

  let projectIdForLoading = getAppCfg().projectId;
  if (serverFeatureAvailable()) {
    try {
      await checkUser(true);
      if (getCloudUser().loggedIn) {
        if (solutionProjectRestricted(projectIdForLoading)) showError("PROJEKT NICHT AUFRUFBAR");

        cleanLocalProjects();
        if (!isCloudProjectId(projectIdForLoading)) {
          const user = getCloudUser();
          const { createCloudId } = await import("../project/projectid");
          
          // First, try new format (with UID_)
          const newCloudId = await createCloudId(user.userName, projectIdForLoading, true);
          const newFormatResult = await loadCloudDataWithCodeCheck(newCloudId, publicProject);
          
          let finalCloudId = newCloudId;
          
          // If new format exists but has no code, try old format
          if (newFormatResult.success && !newFormatResult.hasCode) {
            console.log(`Project ${projectIdForLoading} exists in new format (${newCloudId}) but has no code, checking old format...`);
            
            // Only try old format if user is logged in and has a non-hashed username
            if (user.userName && typeof user.userName === 'string' && !user.userName.startsWith('UID_')) {
              const oldCloudId = await createCloudId(user.userName, projectIdForLoading, false);
              const oldFormatResult = await loadCloudDataWithCodeCheck(oldCloudId, publicProject);
              
              if (oldFormatResult.success && oldFormatResult.hasCode) {
                console.log(`Project ${projectIdForLoading} found with old format (${oldCloudId}) and has code, using old format`);
                finalCloudId = oldCloudId;
              } else if (oldFormatResult.success) {
                console.log(`Project ${projectIdForLoading} old format exists but also has no code`);
              } else {
                console.log(`Project ${projectIdForLoading} old format not found (${oldFormatResult.status === 404 ? '404' : 'error'})`);
              }
            } else {
              console.log(`Cannot check old format: user.userName is ${user.userName ? (typeof user.userName === 'string' && user.userName.startsWith('UID_') ? 'already hashed' : 'not a string') : 'undefined'}`);
            }
          } else if (newFormatResult.success) {
            console.log(`Project ${projectIdForLoading} found in new format (${newCloudId}) with code, using new format`);
          } else {
            console.log(`Project ${projectIdForLoading} new format not found, trying old format...`);
            // If new format doesn't exist, try old format
            if (user.userName && typeof user.userName === 'string' && !user.userName.startsWith('UID_')) {
              const oldCloudId = await createCloudId(user.userName, projectIdForLoading, false);
              const oldFormatResult = await loadCloudDataWithCodeCheck(oldCloudId, publicProject);
              
              if (oldFormatResult.success) {
                console.log(`Project ${projectIdForLoading} found with old format (${oldCloudId}), using old format`);
                finalCloudId = oldCloudId;
              }
            }
          }
          
          projectIdForLoading = finalCloudId;
        }

        const cloudDataResult = await loadCloudData(projectIdForLoading, publicProject);
        if (cloudDataResult.success) {
          await loadDataFromCloud(cloudDataResult.data);
        } else {
          console.log('loadUserData error:', cloudDataResult.error);
          // Set error state for public projects that fail to load (e.g., 404)
          if (publicProject) {
            const cloudStorageData = getCloudData();
            cloudStorageData.loadError = cloudDataResult.status === 404 ? 'INVALID_LINK' : 'LOAD_ERROR';
            setCloudData(cloudStorageData);
          }
        }
        await loadProjectList();
      } else {
        if (solutionProjectRestricted(projectIdForLoading)) showError("PROJEKT NICHT AUFRUFBAR");
      }
    } catch (error) {
      console.error('loadUserData error:', error);
      if (publicProject) {
        const cloudDataResult = await loadCloudData(projectIdForLoading, publicProject);
        if (cloudDataResult.success) {
          await loadDataFromCloud(cloudDataResult.data);
        } else {
          console.log('loadUserData public error:', cloudDataResult.error);
          // Set error state for public projects that fail to load (e.g., 404)
          const cloudStorageData = getCloudData();
          cloudStorageData.loadError = cloudDataResult.status === 404 ? 'INVALID_LINK' : 'LOAD_ERROR';
          setCloudData(cloudStorageData);
        }
      } else {
        if (solutionProjectRestricted(projectIdForLoading)) showError("PROJEKT NICHT AUFRUFBAR");
      }
    }

  } else if (solutionProjectRestricted(projectIdForLoading)) {
    showError("PROJEKT NICHT AUFRUFBAR");
  }

  // Nach Cloud-Versuch `loaded` setzen wenn noch nicht (z. B. 404 / kein Cloud-Eintrag), sonst blockiert executeCodeRequested ewig.
  if (serverFeatureAvailable() && getCloudUser().loggedIn) {
    const cloudStorageData = getCloudData();
    if (cloudStorageData.loaded !== true) {
      cloudStorageData.loaded = true;
      setCloudData(cloudStorageData);
    }
  }

  initResultCode();

}

export async function loadProjectList() {  
  if (serverFeatureAvailable()) {
    try {
      await checkUser(true);
      cleanLocalProjects();
      await loadProjectListData();
      await loadProjectStatus(); 
      await updateProjectListFromCloudData();
    } catch (err) {
      //we dont have handling for this yet
      // TODO: if login not possible, do something nice!
      resetProjectStatus();
    }
  }
}

async function updateProjectListFromCloudData() {
  const dataList = await listData();
  console.log('PL Loader: updating project list from cloud', dataList);
  const savedProjectList = dataList.projectlist?.data?.data?.list || [];
  const currentProjectList = getCloudProjectList().list || [];
  
  // Create a Set of existing project IDs to avoid duplicates
  const existingProjectIds = new Set<string>();
  currentProjectList.forEach(project => {
    if (project.id) {
      existingProjectIds.add(project.id);
    }
  });
  savedProjectList.forEach(project => {
    if (project.id) {
      existingProjectIds.add(project.id);
    }
  });
  
  // Track projects by projectId to prefer new format (UID_) over old format
  const projectMap = new Map<string, {key: string, value: any}>();
  
  for (const [key, value] of Object.entries(dataList)) {
    if (isCloudProjectId(key)) {
      // Skip deleted projects - they should not be added back to the list
      const projectValue = value as any;
      if (projectValue?.deleted === true || projectValue?.data?.deleted === true) {
        console.log(`${key} is deleted, skipping!`);
        continue;
      }
      const projectMeta = getProjectMetaFromCloudId(key);
      const projectId = projectMeta.projectId;
      
      // Skip if project already exists in list
      if (existingProjectIds.has(projectId)) {
        continue;
      }
      
      // Check if we already have this projectId with a different key
      const existingEntry = projectMap.get(projectId);
      if (existingEntry) {
        // Prefer new format (UID_) over old format (codearek:...)
        // Check userId from decoded projectMeta, not the base64-encoded key
        const existingMeta = getProjectMetaFromCloudId(existingEntry.key);
        const existingIsNewFormat = existingMeta.userId.startsWith('UID_');
        const currentIsNewFormat = projectMeta.userId.startsWith('UID_');
        
        if (currentIsNewFormat && !existingIsNewFormat) {
          // Current is new format, existing is old format - replace
          console.log(`Replacing old format entry for ${projectId} (${existingEntry.key}) with new format (${key})`);
          projectMap.set(projectId, {key, value});
        } else if (!currentIsNewFormat && existingIsNewFormat) {
          // Current is old format, existing is new format - keep existing
          console.log(`Keeping new format entry for ${projectId} (${existingEntry.key}), skipping old format (${key})`);
        } else {
          // Both same format, keep first one
          console.log(`Duplicate entry for ${projectId} with same format, keeping first (${existingEntry.key})`);
        }
      } else {
        // New project, add it
        projectMap.set(projectId, {key, value});
      }
    }    
  }
  
  const missingProjects = Array.from(projectMap.values());
  console.log('PL Loader:project lists not consistent!', missingProjects.length, 'missing projects');
  
  let projectListData = [ ...currentProjectList ];
  for(let i = 0; i<missingProjects.length; i++) {
    const missingProject = missingProjects[i];
    const projectMetaData = getProjectMetaFromCloudId(missingProject.key);
    const projectDataForList = {
      id: projectMetaData.projectId,
      shareId : await cloudProjectId(projectMetaData.projectId),
      name: encodeURI("Karte: " + projectMetaData.projectId),
      screenshot: null,
      icon: getIconForProject(projectMetaData.projectId),
      status: missingProject.value.status
    }
    
    // Double-check: don't add if already exists (shouldn't happen, but safety check)
    if (!projectListData.find(p => p.id === projectMetaData.projectId)) {
      projectListData.push(projectDataForList);
    } else {
      console.log(`Skipping duplicate project ${projectMetaData.projectId} - already in list`);
    }
  }
  
  // Final deduplication pass to ensure no duplicates in the final list
  const seenIds = new Set<string>();
  const deduplicatedList = [];
  for (const project of projectListData) {
    if (project.id && !seenIds.has(project.id)) {
      seenIds.add(project.id);
      deduplicatedList.push(project);
    } else if (project.id && seenIds.has(project.id)) {
      console.log(`Removing duplicate project in final pass: ${project.id}`);
    }
  }
  
  if (deduplicatedList.length !== projectListData.length) {
    console.log(`Final deduplication: removed ${projectListData.length - deduplicatedList.length} duplicate projects`);
  }
  
  updateProjectListData({ list: deduplicatedList });
  setCloudProjectList( { list: deduplicatedList });
}

async function loadProjectStatus() {
  const id = getAppCfg().projectId;
  const projects = getCloudProjectList();
  const project = projects.list.find(elm => elm.id === id);
  if (project) {
    const projectShareData = getComponentData(SHARE_PROJECT_STATE);

    projectShareData.sharingStatus = project.status;
    projectShareData.projectId = id;
    projectShareData.shareId = await cloudProjectId(id);
    if (project.status === "public") {
      projectShareData.url = buildPublicUrl(await cloudProjectId(id));
    } else if (project.status === "unkown") {
      projectShareData.sharingStatus = "private";
    }
    setComponentData(SHARE_PROJECT_STATE, projectShareData);
  }
}

export const resetProjectStatus = () => {
  const projectShareData = getComponentData(SHARE_PROJECT_STATE);
  projectShareData.sharingStatus = "private";
  setComponentData(SHARE_PROJECT_STATE, projectShareData);
}

export function projectListId() {
  return "projectlist";
}

async function loadProjectListData() {
 
  // Load data from projectlist endpoint.
  const response = await loadCloudData(projectListId(), false);
  
  if (response.success && response.data) {
    if (response.data.list) {
      const projectList : ProjectListData = {
        list: response.data.list
      }      
      setCloudProjectList(projectList);
    } else if (response.data.length > 0) {
      const projectList : ProjectListData = {
        list: response.data
      }
      setCloudProjectList(projectList);
    } else {
      setCloudProjectList({ list : [] });
    }
  } else {
    setCloudProjectList({ list : [] });
  }
}

export async function statusChangeable() {
  if (serverFeatureAvailable()) {
    await checkUser(true);
    //const response = await listData();
    //setCloudProjectList(response);
    await loadProjectList();
    await loadProjectStatus();
  }
}

// save userData in localstorage
export function bufferUserData() {
  saveUserCodeToLS();
}

export const loadCurrentProjectList = (cloudProjectList, cloudUser) => {
  let projectList = loadLocalProjectList();
  console.log('loadCurrentProjectList cloud: ', cloudProjectList, 'local:', projectList);
  if (cloudProjectList.list && cloudProjectList.list.length > 0 && cloudUser.loggedIn) {    
    projectList = [ ...cloudProjectList.list ];
    
    // Remove duplicates based on project id - keep first occurrence
    const seenIds = new Set<string>();
    const deduplicatedList = [];
    
    for (const project of projectList) {
      if (project.id && !seenIds.has(project.id)) {
        seenIds.add(project.id);
        deduplicatedList.push(project);
      } else if (project.id && seenIds.has(project.id)) {
        console.log(`Removing duplicate project: ${project.id}`);
      }
    }
    
    if (deduplicatedList.length !== projectList.length) {
      console.log(`Removed ${projectList.length - deduplicatedList.length} duplicate projects from list`);
    }
    
    projectList = deduplicatedList;
  }
  return projectList;
}
